import type { GenConfig, OrganizationType } from '../../types';

// ── 頁面上下文 ────────────────────────────────────────────────────────────────

export type PageId =
  | 'toolkit'
  | 'style-transfer'
  | 'gallery'
  | 'stamp-manager'
  | 'settings';

export const PAGE_LABELS: Record<PageId, string> = {
  'toolkit': '里務工具包',
  'style-transfer': '人像風格生成',
  'gallery': '圖片庫',
  'stamp-manager': '印章管理',
  'settings': '系統設定區',
};

export interface QuickGenContext {
  step: number;
  // 舊版固定欄位（保留給舊 pipeline 與相依組件），新 agentic 工具請改用
  // flowModelId + answers 通用結構。
  ratio: string;
  style: string;
  tone: string;
  content: string;
  /** 當前已套用的 FlowModel id；未套用任何 toolkit 時 undefined。 */
  flowModelId?: string;
  /**
   * 當前所有 textual answers (string / boolean) — 鍵為 question id，
   * 給 agentic 工具 (get_active_toolkit_questions / set_quick_gen_answer)
   * 撐起通用的問答機制。
   */
  answers?: Record<string, string | boolean>;
}

export interface UserIdentityState {
  organizationName?: string | null;
  organizationType: OrganizationType;
  name: string;
  title?: string | null;
}

export interface PageContext {
  pageId: PageId;
  pageLabel: string;
  globalState: UserIdentityState;
  quickGen?: QuickGenContext;
  settings?: UserIdentityState;
}

// ── AI 可執行動作 ──────────────────────────────────────────────────────────────

export type AssistantEventName =
  | 'assistant:quick-gen:set-step'
  | 'assistant:quick-gen:set-answer'
  | 'assistant:quick-gen:trigger-generate'
  | 'assistant:style-transfer:select-style'
  | 'assistant:toolkit:filter-category'
  | 'assistant:toolkit:apply-template';

export type AssistantAction =
  | { type: 'navigate'; tab: string; description: string; autoExecute?: boolean }
  | { type: 'set-gen-config'; payload: Partial<GenConfig>; description: string; autoExecute?: boolean }
  | { type: 'set-gen-step'; step: 1 | 2 | 3 | 4; description: string; autoExecute?: boolean }
  | { type: 'set-global-setting'; payload: { organizationName?: string | null; organizationType?: OrganizationType; name?: string; title?: string | null }; description: string; autoExecute?: boolean }
  | { type: 'dispatch-event'; eventName: AssistantEventName; detail: Record<string, unknown>; description: string; autoExecute?: boolean }
  | { type: 'hint'; message: string; description: string; autoExecute?: boolean };

// ── AI 回覆格式 ───────────────────────────────────────────────────────────────

export interface AssistantResponse {
  text: string;
  actions: AssistantAction[];
}

// ── 對話訊息 ──────────────────────────────────────────────────────────────────

/** 從 API 回傳的 action 格式（flat 結構，與 discriminated union 相容） */
export interface ApiAction {
  type: 'navigate' | 'set-gen-config' | 'set-gen-step' | 'set-global-setting' | 'dispatch-event' | 'hint';
  description: string;
  autoExecute?: boolean;
  tab?: string;
  payload?: Record<string, unknown>;
  step?: number;
  eventName?: string;
  detail?: Record<string, unknown>;
  message?: string;
}

export interface AssistantChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  executedLabels?: string[];
  pendingActions?: ApiAction[];
  timestamp: Date;
}
